/**
 * FormationBot - Abstract base class for formation-based bots (IMPOSSIBLE mode).
 * Extends BotBase with formation anchor behavior and stretch zones.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { BotBase } from "./BotBase";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";

// Formation-specific constants
const FORMATION_RETURN_SPEED = 4.0;
const ANCHOR_ATTRACTION_DISTANCE = 10;
const FORMATION_SHAPE_STRENGTH = 0.3;

/**
 * Abstract base class for formation-based bots (IMPOSSIBLE mode)
 * - Adds formation anchor behavior
 * - Implements vertical/horizontal stretch from home position
 * - Coordinates with teammates based on role
 */
export abstract class FormationBot extends BotBase {
  protected _formationAnchor: BABYLON.Vector3;
  protected _verticalStretch: number = 10;
  protected _horizontalStretch: number = 8;
  protected _roleCategory: "defender" | "midfielder" | "forward";

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    role: AIRole,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player,
    meshName: string = "formationBot"
  ) {
    super(
      scene,
      ball,
      role,
      homePosition,
      allowedZone,
      separationConfig,
      shadowGenerator,
      player,
      meshName
    );
    
    this._formationAnchor = homePosition.clone();
    this._roleCategory = this._determineRoleCategory(role);
  }

  /**
   * Determine role category from AIRole
   */
  private _determineRoleCategory(role: AIRole): "defender" | "midfielder" | "forward" {
    switch (role) {
      case AIRole.LEFT_BACK:
      case AIRole.CENTER_BACK_LEFT:
      case AIRole.CENTER_BACK_RIGHT:
      case AIRole.RIGHT_BACK:
        return "defender";
      case AIRole.DEFENSIVE_MID:
      case AIRole.CENTRAL_MID:
      case AIRole.ATTACKING_MID:
        return "midfielder";
      case AIRole.LEFT_WINGER:
      case AIRole.RIGHT_WINGER:
      case AIRole.CENTER_FORWARD:
        return "forward";
      default:
        return "midfielder";
    }
  }

  /**
   * Get role category
   */
  get roleCategory(): "defender" | "midfielder" | "forward" {
    return this._roleCategory;
  }

  /**
   * Set stretch values for formation positioning
   */
  setStretch(vertical: number, horizontal: number): void {
    this._verticalStretch = vertical;
    this._horizontalStretch = horizontal;
  }

  /**
   * Calculate stretch zone based on anchor and stretch values
   */
  protected _calculateStretchZone(): AllowedZone {
    return {
      minX: this._formationAnchor.x - this._horizontalStretch,
      maxX: this._formationAnchor.x + this._horizontalStretch,
      minZ: this._formationAnchor.z - this._verticalStretch,
      maxZ: this._formationAnchor.z + this._verticalStretch
    };
  }

  /**
   * Check if bot should return to formation
   */
  protected _shouldReturnToFormation(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3): boolean {
    const distToAnchor = BABYLON.Vector3.Distance(myPos, this._formationAnchor);
    const distToBall = BABYLON.Vector3.Distance(myPos, ballPos);
    
    // Return to formation if far from anchor and ball is far
    return distToAnchor > ANCHOR_ATTRACTION_DISTANCE && distToBall > 15;
  }

  /**
   * Handle formation return behavior
   */
  protected _handleFormationReturn(dt: number): void {
    const myPos = this.mesh.position;
    const anchor = this._formationAnchor;
    
    const dx = anchor.x - myPos.x;
    const dz = anchor.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.5) {
      myPos.x += (dx / dist) * FORMATION_RETURN_SPEED * dt;
      myPos.z += (dz / dist) * FORMATION_RETURN_SPEED * dt;
      this._currentSpeed = FORMATION_RETURN_SPEED;
      
      const angle = Math.atan2(dx, dz);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    } else {
      this._currentSpeed = 0;
      this._state = AIState.HOLD_POSITION;
    }
  }

  /**
   * Get dynamic position based on ball position
   * Moves formation position slightly based on ball
   */
  protected _getDynamicPosition(ballPos: BABYLON.Vector3): BABYLON.Vector3 {
    const stretchZone = this._calculateStretchZone();
    let targetX = this._formationAnchor.x;
    let targetZ = this._formationAnchor.z;
    
    // Shift position based on ball location
    const ballInfluenceX = (ballPos.x - 0) * FORMATION_SHAPE_STRENGTH;
    const ballInfluenceZ = (ballPos.z - 0) * FORMATION_SHAPE_STRENGTH * 0.5;
    
    // Different behavior based on role category
    switch (this._roleCategory) {
      case "defender":
        // Defenders track ball horizontally but stay deep
        targetX = this._formationAnchor.x + ballInfluenceX * 0.7;
        targetZ = this._formationAnchor.z + Math.min(ballInfluenceZ, 0); // Don't push up
        break;
      case "midfielder":
        // Midfielders move with the ball more
        targetX = this._formationAnchor.x + ballInfluenceX;
        targetZ = this._formationAnchor.z + ballInfluenceZ;
        break;
      case "forward":
        // Forwards stay high and wide
        targetX = this._formationAnchor.x + ballInfluenceX * 0.5;
        targetZ = this._formationAnchor.z + Math.max(ballInfluenceZ * 0.3, -3); // Stay high
        break;
    }
    
    // Clamp to stretch zone
    targetX = BABYLON.Scalar.Clamp(targetX, stretchZone.minX, stretchZone.maxX);
    targetZ = BABYLON.Scalar.Clamp(targetZ, stretchZone.minZ, stretchZone.maxZ);
    
    return new BABYLON.Vector3(targetX, 0, targetZ);
  }

  /**
   * Override update to add formation behavior
   */
  update(dt: number, teammates: BotBase[]): void {
    // Call parent update
    super.update(dt, teammates);
  }

  /**
   * Handle formation-specific states
   */
  protected _handleState(
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3,
    distToBall: number,
    distToGoal: number,
    dt: number
  ): void {
    // Handle formation-specific states
    switch (this._state) {
      case AIState.FORMATION_RETURN:
        this._handleFormationReturn(dt);
        return;
      case AIState.HOLD_POSITION:
        this._handleHoldPosition(ballPos, myPos, dt);
        return;
    }
    
    // Fall back to parent handler
    super._handleState(ballPos, myPos, distToBall, distToGoal, dt);
  }

  /**
   * Hold position near formation anchor
   */
  protected _handleHoldPosition(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    const targetPos = this._getDynamicPosition(ballPos);
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.5) {
      const speed = FORMATION_RETURN_SPEED * 0.5;
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;
    } else {
      this._currentSpeed = 0;
    }
    
    // Face the ball
    const toBall = ballPos.subtract(myPos);
    const angle = Math.atan2(toBall.x, toBall.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.05);
  }

  /**
   * Reset formation position
   */
  resetPosition(): void {
    super.resetPosition();
  }
}